import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Plus, X, Calendar, Flame, Zap } from 'lucide-react';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/useToast';
import { useAppContext } from '@/hooks/useAppContext';

export function CreatePollDialog() {
  const { user } = useCurrentUser();
  const { mutate: createPoll, isPending } = useNostrPublish();
  const { toast } = useToast();
  const { config } = useAppContext();

  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [isMultiChoice, setIsMultiChoice] = useState(false);
  const [hasExpiry, setHasExpiry] = useState(false);
  const [expiryDays, setExpiryDays] = useState('7');

  const handleAddOption = () => {
    if (options.length < 10) {
      setOptions([...options, '']);
    }
  };

  const handleRemoveOption = (index: number) => {
    if (options.length > 2) {
      setOptions(options.filter((_, i) => i !== index));
    }
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const generateOptionId = () => {
    return Math.random().toString(36).substring(2, 11);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast({
        title: 'Login required',
        description: 'Please login to create a poll',
        variant: 'destructive',
      });
      return;
    }

    const validOptions = options.filter(opt => opt.trim().length > 0);

    if (!question.trim()) {
      toast({
        title: 'Question required',
        description: 'Please enter a poll question',
        variant: 'destructive',
      });
      return;
    }

    if (validOptions.length < 2) {
      toast({
        title: 'Insufficient options',
        description: 'Please provide at least 2 options',
        variant: 'destructive',
      });
      return;
    }

    const tags: string[][] = [];

    // Add option tags
    validOptions.forEach(opt => {
      tags.push(['option', generateOptionId(), opt]);
    });

    // Add relay tags from config
    const relays = config.relayMetadata?.relays || [];
    relays
      .filter(r => r.write)
      .slice(0, 5)
      .forEach(relay => {
        tags.push(['relay', relay.url]);
      });

    // Add poll type
    tags.push(['polltype', isMultiChoice ? 'multiplechoice' : 'singlechoice']);

    // Add expiry if enabled
    if (hasExpiry) {
      const days = parseInt(expiryDays) || 7;
      const expiryTimestamp = Math.floor(Date.now() / 1000) + (days * 24 * 60 * 60);
      tags.push(['endsAt', expiryTimestamp.toString()]);
    }

    createPoll(
      {
        kind: 1068,
        content: question.trim(),
        tags,
      },
      {
        onSuccess: () => {
          toast({
            title: '🔥 Poll created!',
            description: 'Your poll has been published to Nostr',
          });
          setOpen(false);
          setQuestion('');
          setOptions(['', '']);
          setIsMultiChoice(false);
          setHasExpiry(false);
          setExpiryDays('7');
        },
        onError: () => {
          toast({
            title: 'Failed to create poll',
            description: 'Please try again',
            variant: 'destructive',
          });
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="default"
          className="gap-2 shadow-lg animate-scale-in bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:shadow-xl hover:shadow-orange-500/30 transition-all duration-300 border-0 font-black hover:scale-105 text-white relative overflow-hidden group"
        >
          {/* Animated background shimmer */}
          <div className="absolute inset-0 bg-gradient-to-r from-orange-600 via-amber-600 to-orange-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <Flame className="h-4 w-4 relative z-10 animate-fire-dance" />
          <span className="hidden sm:inline relative z-10">Create Poll</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto glass dark:glass-dark border border-orange-500/30 shadow-2xl shadow-orange-500/10">
        <DialogHeader>
          <DialogTitle className="text-3xl font-black flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl blur-lg opacity-50 animate-breathe" />
              <div className="relative bg-gradient-to-br from-orange-500 to-amber-500 p-2 rounded-xl">
                <Flame className="h-6 w-6 text-white animate-fire-dance" />
              </div>
            </div>
            <span className="bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 bg-clip-text text-transparent">
              Create a Poll
            </span>
          </DialogTitle>
          <DialogDescription className="text-base font-medium text-foreground/60">
            Ask a question and let the Nostr community vote
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 pt-4">
          <div className="space-y-2">
            <Label htmlFor="question" className="text-base font-bold">
              Question
            </Label>
            <Input
              id="question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="What's your question?"
              className="text-base glass dark:glass-dark border border-orange-500/20 focus:border-orange-500/60 focus:ring-orange-500/20 font-medium transition-all duration-300"
              maxLength={280}
            />
            <p className="text-xs text-foreground/50 font-medium">
              {question.length}/280 characters
            </p>
          </div>

          <div className="space-y-3">
            <Label className="text-base font-bold">Options</Label>
            {options.map((option, index) => (
              <div key={index} className="flex gap-2 animate-slide-up" style={{ animationDelay: `${index * 0.05}s` }}>
                <Input
                  value={option}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  placeholder={`Option ${index + 1}`}
                  maxLength={100}
                  className="glass dark:glass-dark border border-orange-500/20 focus:border-orange-500/60 focus:ring-orange-500/20 font-medium transition-all duration-300"
                />
                {options.length > 2 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveOption(index)}
                    className="shrink-0 hover:bg-red-500/10 hover:text-red-500 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            {options.length < 10 && (
              <Button
                type="button"
                variant="outline"
                onClick={handleAddOption}
                className="w-full glass dark:glass-dark border border-orange-500/20 hover:bg-orange-500/10 hover:border-orange-500/40 font-bold transition-all duration-300 group"
              >
                <Plus className="h-4 w-4 mr-2 group-hover:rotate-90 transition-transform duration-300" />
                Add Option
              </Button>
            )}
          </div>

          <div className="space-y-4 pt-4 border-t border-orange-500/10">
            <div className="flex items-center justify-between p-4 rounded-xl glass dark:glass-dark border border-orange-500/10 hover:border-orange-500/20 transition-all duration-300">
              <div className="space-y-0.5">
                <Label htmlFor="multichoice" className="text-base font-bold cursor-pointer">
                  Multiple Choice
                </Label>
                <p className="text-sm text-foreground/50">
                  Allow voters to select multiple options
                </p>
              </div>
              <Switch
                id="multichoice"
                checked={isMultiChoice}
                onCheckedChange={setIsMultiChoice}
                className="data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-orange-500 data-[state=checked]:to-amber-500"
              />
            </div>

            <div className="flex items-center justify-between p-4 rounded-xl glass dark:glass-dark border border-orange-500/10 hover:border-orange-500/20 transition-all duration-300">
              <div className="space-y-0.5">
                <Label htmlFor="expiry" className="text-base font-bold cursor-pointer">
                  Set Expiry
                </Label>
                <p className="text-sm text-foreground/50">
                  Poll will close after specified days
                </p>
              </div>
              <Switch
                id="expiry"
                checked={hasExpiry}
                onCheckedChange={setHasExpiry}
                className="data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-orange-500 data-[state=checked]:to-amber-500"
              />
            </div>

            {hasExpiry && (
              <div className="space-y-2 ml-4 animate-slide-up p-4 rounded-xl bg-orange-500/5 border border-orange-500/10">
                <Label htmlFor="days" className="text-sm font-bold flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-orange-500" />
                  Days until expiry
                </Label>
                <Input
                  id="days"
                  type="number"
                  min="1"
                  max="365"
                  value={expiryDays}
                  onChange={(e) => setExpiryDays(e.target.value)}
                  className="max-w-32 glass dark:glass-dark border border-orange-500/20 focus:border-orange-500/60 focus:ring-orange-500/20 font-bold"
                />
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="flex-1 glass dark:glass-dark border border-orange-500/20 hover:bg-orange-500/10 hover:border-orange-500/40 font-bold transition-all duration-300"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isPending || !user}
              className="flex-1 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:shadow-xl hover:shadow-orange-500/30 transition-all duration-300 border-0 font-black text-white relative overflow-hidden group"
            >
              {/* Animated background */}
              <div className="absolute inset-0 bg-gradient-to-r from-orange-600 via-amber-600 to-orange-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              {isPending ? (
                <span className="flex items-center gap-2 relative z-10">
                  <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating...
                </span>
              ) : (
                <span className="flex items-center gap-2 relative z-10">
                  <Zap className="h-4 w-4" />
                  Create Poll
                </span>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
